import gsap from 'gsap'
import { Point } from 'pixi.js'
import { isIOS, isMobileOrTablet } from '@/scripts/helpers/features'
import resizer from '@/scripts/canvas/resizer'
import type { Activity } from '@/scripts/data/types'
import { events } from '@/scripts/config/events'
import { createActivityView } from './activityView'
import { createActivityViewAnalytics } from './activityViewConfig'

function getCanvasRect(): DOMRect | null {
  const canvas = document.querySelector('.canvas-container canvas') as HTMLCanvasElement | null
  return canvas?.getBoundingClientRect() ?? null
}

function quadrantFromViewport(vp: Point): 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center' {
  const crect = getCanvasRect()
  if (!crect) return 'center'
  const cx = crect.left + crect.width / 2
  const cy = crect.top + crect.height / 2
  const right = vp.x >= cx
  const bottom = vp.y >= cy
  if (!right && !bottom) return 'top-left'
  if (right && !bottom) return 'top-right'
  if (!right && bottom) return 'bottom-left'
  return 'bottom-right'
}

function viewportToOffsetParent(el: HTMLElement, vp: Point): Point {
  const parent = (el.offsetParent as HTMLElement) ?? document.documentElement
  const prect = parent.getBoundingClientRect()
  return new Point(vp.x - prect.left, vp.y - prect.top)
}

let root: HTMLDivElement
let view: ReturnType<typeof createActivityView>
let visible = false
let positionProvider: (() => Point) | null = null
let onWinResize = () => {}
let onResizer = () => {}

let _onShow: () => void = () => {}
let _onHide: () => void = () => {}

let __prevMode: 'none' | 'passive' | 'auto' | 'static' | 'dynamic' | null = null
let __prevInteractiveChildren: boolean | null = null

function disablePixiWhileOverBubble() {
  const app = __PIXI_APP__
  if (!app?.stage) return
  if (__prevMode === null) __prevMode = app.stage.eventMode
  if (__prevInteractiveChildren === null) __prevInteractiveChildren = app.stage.interactiveChildren
  app.stage.eventMode = 'none'
  app.stage.interactiveChildren = false
}

function restorePixiAfterBubble() {
  const app = __PIXI_APP__
  if (!app?.stage) return
  app.stage.eventMode = __prevMode !== null ? __prevMode : 'static'
  app.stage.interactiveChildren = __prevInteractiveChildren !== null ? __prevInteractiveChildren : true
  __prevMode = null
  __prevInteractiveChildren = null
}

function applyViewportPoint(vp: Point) {
  const local = viewportToOffsetParent(root, vp)
  root.style.left = `${local.x}px`
  root.style.top = `${local.y}px`
  root.setAttribute('data-quadrant', quadrantFromViewport(vp))
}

function updateFromProvider() {
  if (positionProvider) applyViewportPoint(positionProvider())
}

function runShowAnimation() {
  view.showRoot()
  gsap.killTweensOf(root)
  gsap.set(root, { opacity: 0, scale: 0.5, transformOrigin: '50% 50%' })
  gsap.to(root, { duration: 0.4, opacity: 1, ease: 'sine.out' })
  gsap.to(root, { duration: 0.4, scale: 1, ease: 'back.out(1.5)' })
}

function runHideAnimation() {
  gsap.killTweensOf(root)
  gsap.to(root, { duration: 0.3, opacity: 0, onComplete: () => view.hideRoot() })
  gsap.to(root, { duration: 0.3, scale: 0.4, ease: 'sine.in' })
}

function init(el: HTMLDivElement, { onShow, onHide }: { onShow: () => void; onHide: () => void }) {
  root = el
  _onShow = onShow
  _onHide = onHide

  view = createActivityView(root, {
    isMobileOrTablet,
    isIOS,
    analytics: createActivityViewAnalytics(),
  })

  const closeBtn = root.querySelector<HTMLButtonElement>('.close-button')

  root.addEventListener('pointerenter', () => disablePixiWhileOverBubble())
  root.addEventListener('pointerleave', () => restorePixiAfterBubble())

  document.addEventListener('activity-info-box:hide', () => {
    restorePixiAfterBubble()
    hide()
  })

  closeBtn?.addEventListener('click', (e) => {
    e.preventDefault()
    hide()
  })
}

function showWithProvider(activity: Activity, provider: () => Point) {
  if (activity) view.setActivity(activity)

  positionProvider = provider
  updateFromProvider()
  root.style.transform = ''

  onWinResize = updateFromProvider
  onResizer = updateFromProvider

  window.addEventListener('resize', onWinResize)
  resizer.on(events.RESIZE, onResizer)

  visible = true
  _onShow()
  runShowAnimation()
}

function showAtViewport(activity: Activity, viewportPoint: Point) {
  if (activity) view.setActivity(activity)
  applyViewportPoint(viewportPoint)
  root.style.transform = ''
  visible = true
  _onShow()
  runShowAnimation()
}

function show(activity: Activity, containerPoint: Point) {
  const containerEl = document.querySelector('.canvas-container') as HTMLElement | null
  const crect = containerEl?.getBoundingClientRect()
  if (!crect) return
  const vp = new Point(crect.left + containerPoint.x, crect.top + containerPoint.y)
  showAtViewport(activity, vp)
}

function hide() {
  if (!visible) return
  visible = false

  runHideAnimation()

  window.removeEventListener('resize', onWinResize)
  resizer.off?.(events.RESIZE, onResizer)
  positionProvider = null
  _onHide()
}

export default {
  init,
  show,
  showAtViewport,
  showWithProvider,
  hide,
  isVisible: () => visible,
  getActivity: () => view.getActivity(),
}
